const unwrap = (payload) => payload?.data ?? payload ?? {};

const toNumber = (value) => {
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
};

const normalizeDistribution = (items = []) => {
	if (!Array.isArray(items)) return [];

	return items.map((item) => {
		if (typeof item === "string") {
			return { label: item, count: 0 };
		}
		return {
			label: item.label ?? item.name ?? item.answer ?? item.option ?? "-",
			count: Number(item.count ?? item.total ?? item.value ?? 0),
		};
	});
};

const normalizePagination = (rawPayload, body) => {
	const pagination =
		body.pagination ??
		body.meta ??
		rawPayload?.pagination ??
		rawPayload?.meta ??
		null;

	if (!pagination) return null;

	return {
		current_page: pagination.current_page ?? pagination.currentPage ?? 1,
		last_page: pagination.last_page ?? pagination.lastPage ?? 1,
		per_page: pagination.per_page ?? pagination.perPage ?? 15,
		total: pagination.total ?? 0,
		from: pagination.from ?? 1,
		to: pagination.to ?? 0,
	};
};

const CHART_KEYS = {
	q5: "q5_features",
	q6: "q6_interest",
	q7: "q7_recommendation",
};

const pickSubmittedAt = (row) =>
	row?.last_survey_at ??
	row?.submitted_at ??
	row?.completed_at ??
	row?.updated_at ??
	row?.created_at ??
	null;

const pickLatestTimestamp = (items = []) => {
	const timestamps = items
		.map((item) => item?.updated_at ?? item?.created_at)
		.filter(Boolean);

	if (timestamps.length === 0) return null;

	return timestamps.sort((a, b) => new Date(b) - new Date(a))[0];
};

const questionNumber = (code) => {
	const match = code?.match(/q(\d+)$/);
	return match ? Number(match[1]) : 0;
};

const isScaleQuestion = (code) => /^app_survey_q[1-4]$/.test(code ?? "");

const formatAnswer = (item) => {
	const answer = item?.answer ?? "-";
	if (isScaleQuestion(item?.question_code)) {
		const num = Number(answer);
		if (Number.isFinite(num)) return `${num} / 5`;
	}
	return answer;
};

const normalizeSurveyAnswers = (answers = []) => {
	return [...answers]
		.sort((a, b) => questionNumber(a.question_code) - questionNumber(b.question_code))
		.map((item) => ({
			code: item.question_code,
			title: item.question_title ?? "-",
			answer: item.answer ?? "-",
			displayAnswer: formatAnswer(item),
			isEssay: questionNumber(item.question_code) >= 8,
		}));
};

export const normalizeAnalytics = (rawPayload) => {
	const body = unwrap(rawPayload);
	const averagesSource = body.averages ?? {};
	const chartsSource = body.charts ?? {};

	return {
		totalRespondents: Number(body.total_respondents ?? 0),
		averages: {
			ease: toNumber(averagesSource.ease),
			performance: toNumber(averagesSource.performance),
			stability: toNumber(averagesSource.stability),
			satisfaction: toNumber(averagesSource.satisfaction),
		},
		charts: {
			q5: normalizeDistribution(chartsSource[CHART_KEYS.q5] ?? []),
			q6: normalizeDistribution(chartsSource[CHART_KEYS.q6] ?? []),
			q7: normalizeDistribution(chartsSource[CHART_KEYS.q7] ?? []),
		},
	};
};

export const normalizeRespondentsList = (rawPayload) => {
	const body = unwrap(rawPayload);
	const rows = Array.isArray(body) ? body : body.data ?? [];
	const pagination = normalizePagination(rawPayload, body);

	const data = rows.map((row) => ({
		userId: row.user_id ?? row.userId ?? row.id,
		name: row.name ?? row.user?.name ?? "-",
		email: row.email ?? row.user?.email ?? "-",
		submittedAt: pickSubmittedAt(row),
	}));

	return { data, pagination };
};

export const normalizeRespondentDetail = (rawPayload) => {
	const body = unwrap(rawPayload);
	const user = body.user ?? {};
	const answers = body.answers ?? body.surveys ?? body.responses ?? body.essays ?? [];

	return {
		userId: user.id ?? body.user_id ?? body.userId ?? body.id,
		name: user.name ?? body.name ?? "-",
		email: user.email ?? body.email ?? "-",
		submittedAt:
			pickSubmittedAt(body) ??
			pickSubmittedAt(user) ??
			pickLatestTimestamp(answers),
		answers: normalizeSurveyAnswers(answers),
	};
};
